import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CommandBase, FilteredCommand, MultiselectCommand } from '../../../core/model/command';
import { ICommandHandler } from '../../../core/service/command/command-handler-resolver.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import {
  leaseAgreementCommand,
  LeaseAgreementFilteredCommand,
  LeaseAgreementMultiselectCommand,
} from '../../command/lease-agreement.command';
import { LeaseAgreementCategory } from '../../model/const/enums';
import { DwellingContractService } from '../data/dwelling-contract.service';
import { ParkingContractService } from '../data/parking-contract.service';
import { PremisesContractService } from '../data/premises-contract.service';
import { OtherContractService } from '../data/other-contract.service';
import { BlockContractService } from '../data/block-contract.service';
import { CooperativeHousingContractService } from '../data/cooperative-housing-contract.service';
import { LeaseAgreementListService } from '../data/lease-agreement-list.service';

@Injectable()
export class LeaseAgreementExportNoPersonalDataCommandHandler implements ICommandHandler {
  constructor(
    private _dwellingService: DwellingContractService,
    private _parkingService: ParkingContractService,
    private _premisesService: PremisesContractService,
    private _otherService: OtherContractService,
    private _blockService: BlockContractService,
    private _cooperativeService: CooperativeHousingContractService,
    private _nativeWindow: NativeWindowService
  ) {}

  canExecute(command: CommandBase): boolean {
    if (command instanceof MultiselectCommand) {
      return command.id === leaseAgreementCommand.exportNoPersonalData && command.itemsSource.length > 0;
    }
    if (command instanceof FilteredCommand) {
      return command.id === leaseAgreementCommand.exportNoPersonalData && command.totalAffectedItemsControlValue > 0;
    }
    return false;
  }

  execute(command: CommandBase): Observable<void> {
    if (command instanceof MultiselectCommand) {
      let categoryCommand = command as LeaseAgreementMultiselectCommand;
      return this._getService(categoryCommand.category)
        .exportNoPersonalData(command.items.map(item => item.id))
        .pipe(
          map(file => {
            this._nativeWindow.save(file);
          })
        );
    }
    if (command instanceof FilteredCommand) {
      let categoryCommand = command as LeaseAgreementFilteredCommand;
      const categoryService = this._getService(categoryCommand.category);
      const exportDataService = categoryCommand.category === LeaseAgreementCategory.CooperativeHousing
        ? categoryService
        : command.dataService ?? categoryService;
      const exportRequest: Observable<File> = exportDataService instanceof LeaseAgreementListService
        ? exportDataService.exportFilteredNoPersonalData(
            command.filterProvider.filterState,
            command.includedIds,
            command.excludedIds,
            command.totalAffectedItemsControlValue,
            categoryService.exportFilename
          )
        : exportDataService.exportFilteredNoPersonalData(
            command.filterProvider.filterState,
            command.includedIds,
            command.excludedIds,
            command.totalAffectedItemsControlValue
          );

      return exportRequest
        .pipe(
          map(file => {
            this._nativeWindow.save(file);
          })
        );
    }
  }

  private _getService(category: LeaseAgreementCategory) {
    switch (category) {
      case LeaseAgreementCategory.Parking:
        return this._parkingService;
      case LeaseAgreementCategory.Premises:
        return this._premisesService;
      case LeaseAgreementCategory.Dwelling:
        return this._dwellingService;
      case LeaseAgreementCategory.Other:
        return this._otherService;
      case LeaseAgreementCategory.Block:
        return this._blockService;
      case LeaseAgreementCategory.CooperativeHousing:
        return this._cooperativeService;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
  }
}
